// Component: StockVideoAIModal
// Purpose: Modal that accepts a natural-language prompt and calls the backend
// AI to generate initial metadata for a stock video. On success, returns the
// generated data to the parent via onGenerateSuccess.
// Props:
// - isOpen: boolean to control visibility
// - onClose: () => void to close the modal
// - onGenerateSuccess: (stockVideoData) => void callback with generated data
import React, { useState, useEffect } from 'react';
import { X, Sparkles } from 'lucide-react';
import { Label } from '../../ui/inputs/label';
import { ButtonSpinner } from '../../ui/feedback/loading-spinner.jsx';
import { generateStockVideoWithAI } from '../../../api/media/stockVideosApi';

export function StockVideoAIModal({ isOpen, onClose, onGenerateSuccess }) {
  const [aiPrompt, setAiPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [localError, setLocalError] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [generatedData, setGeneratedData] = useState(null);
  
  // Auto-clear error after 5 seconds
  useEffect(() => {
    if (localError) {
      const timer = setTimeout(() => {
        setLocalError('');
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [localError]);
  
  // Handle AI generation
  const handleAIGenerate = async () => {
    if (!aiPrompt.trim()) {
      setLocalError('Please enter a video description to generate stock video data');
      return;
    }
    
    setIsGenerating(true);
    setLocalError('');
    
    try {
      const data = await generateStockVideoWithAI(aiPrompt);
      
      if (data.status === 'error') {
        throw new Error(data.message || 'Failed to generate stock video data');
      }
      
      // Success - pass the data to the parent component
      onGenerateSuccess(data.stockVideoData);
      setGeneratedData(data.stockVideoData);
      onClose();
    } catch (error) {
      console.error('AI generation error:', error);
      setLocalError(error.message || 'Failed to generate stock video data');
    } finally {
      setIsGenerating(false);
    }
  };
  
  // Check if prompt has content to enable the button
  const canSubmit = aiPrompt && aiPrompt.trim();
  
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-900 mx-4">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
        >
          <X className="h-5 w-5" />
        </button>
        
        <div className="mb-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/50">
              <Sparkles className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                AI Stock Video Generator
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Describe your video and let AI fill in the details
              </p>
            </div>
          </div>
        </div>
        
        <div className="space-y-4">
          <div>
            <Label htmlFor="ai-prompt">Video Description</Label>
            <textarea
              id="ai-prompt"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder="Example: A cinematic campaign video showcasing Republican Governor Ron DeSantis talking about Florida's economic growth and conservative values. The video shows scenes of bustling Tampa Bay businesses, families at the beach, and DeSantis speaking at a podium with American flags. Keywords: florida, governor, republican, conservative, economy, growth, families, america first, leadership, tampa bay"
              className="mt-2 block h-40 w-full rounded-lg border border-gray-200 bg-white px-4 py-3 text-gray-900 placeholder:text-gray-500 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/10 dark:border-gray-800 dark:bg-gray-900 dark:text-white dark:placeholder:text-gray-400 dark:focus:border-primary-400 dark:focus:ring-primary-400/10"
            />
          </div>
          
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Tags</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {selectedTags.map(tag => (
                <span key={tag} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
                  {tag}
                </span>
              ))}
            </div>
          </div>
          
          {localError && (
            <div className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/50 dark:text-red-400">
              <X className="h-4 w-4" />
              {localError}
            </div>
          )}
          
          <div className="flex items-center gap-3 rounded-lg border border-primary-100 bg-primary-50/50 p-3 dark:border-primary-900/50 dark:bg-primary-900/10">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/50">
              <svg className="h-4 w-4 text-primary-600 dark:text-primary-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
              </svg>
            </div>
            <p className="text-sm text-primary-700 dark:text-primary-400">
              Describe your video concept like a Veo3 or Sora prompt. Include visual details, people, locations, and key messages. AI will extract title, tags, and search terms.
            </p>
          </div>
          
          {generatedData && (
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Generated Search Text</label>
              <div className="mt-1 p-2 bg-gray-50 dark:bg-gray-800 rounded-md border">
                <p className="text-sm text-gray-900 dark:text-gray-100">{generatedData.searchableText}</p>
              </div>
            </div>
          )}
          
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAIGenerate}
              disabled={!canSubmit || isGenerating}
              className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-500 disabled:bg-primary-500 disabled:opacity-50"
            >
              {isGenerating ? (
                <>
                  <ButtonSpinner color="white" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" />
                  Generate Stock Video
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
} 